
// // ##############################################################

import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import Backdrop from "@mui/material/Backdrop";
import Slide from "@mui/material/Slide";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EntityCard from "examples/Cards/EntityCard";
import EntityDetailsPopup from "layouts/components/EntityDetailsPopup";

const ITEMS_PER_PAGE = 4;

// Helper to get correct field name based on platform
const getContentField = (platform) => (platform === "YouTube" ? "videos" : "posts");

// ⬇️ ADDED: This helper is now needed here
const getContentType = (item, platform) => {
  if (platform === "Facebook") {
    return item.post_type;
  }
  if (platform === "YouTube") {
    return item.video_type;
  }
  if (platform === "Instagram") {
    return item.media_type;
  }
  return "Unknown";
};


// ⬇️ UPDATED: Added filterDayOfWeek
function EntitiesPopup({
  isModalOpen,
  onClose,
  datasets,
  date = null,
  filterProfileId = null,
  filterContentType = null,
  filterDayOfWeek = null,
  filterSentiment = null // ✅ New prop
}) {
  const [showCard, setShowCard] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageData, setPageData] = useState([]);
  const imageHeightsRef = useRef([]);
  const [forcedHeight, setForcedHeight] = useState(250);

  // (Animation states are unchanged)
  const [isTransitioningToDetails, setIsTransitioningToDetails] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // (useEffect for modal open/close is unchanged)
  useEffect(() => {
    if (isModalOpen) {
      setShowCard(true);
    } else {
      const timeout = setTimeout(() => setShowCard(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isModalOpen]);

  const handleCloseClick = () => {
    setShowCard(false);
    setTimeout(onClose, 300);
  };

  // ⬇️=========================================
  // ⬇️ UPDATED: Flattened entities logic
  // ⬇️=========================================
  const flattenedEntities = useMemo(() => {
    if (!datasets) return [];

    // ✅ MODE 1: Filter by Content Type
    if (filterProfileId && filterContentType) {
      const profile = datasets.find(p => p.profileId === filterProfileId);
      if (!profile) return [];
      const contentField = getContentField(profile.platform);
      const entityType = profile.platform === "YouTube" ? "video" : "post";
      const allEntities = (profile.data || []).flatMap((entry) => entry[contentField] || []);
      const filteredEntities = allEntities.filter(content => {
        const type = getContentType(content, profile.platform);
        return type === filterContentType;
      });
      return filteredEntities.map((content) => ({
        ...content,
        profile,
        type: entityType,
      }));
    }

    // ✅ MODE 2: Filter by Sentiment (NEW)
    if (filterProfileId && filterSentiment) {
      const profile = datasets.find(p => p.profileId === filterProfileId);
      if (!profile) return [];
      const contentField = getContentField(profile.platform);
      const entityType = profile.platform === "YouTube" ? "video" : "post";

      const allEntities = (profile.data || []).flatMap((entry) => entry[contentField] || []);

      const filteredEntities = allEntities.filter(content => {
        const comments = content.comments || [];
        let pos = 0;
        let neg = 0;
        let neu = 0;

        comments.forEach(c => {
          const s = c.sentiment?.zoneScore;
          if (typeof s === 'number') {
            if (s > 60) pos++;
            else if (s < 40) neg++;
            else neu++;
          }
        });

        if (filterSentiment === "Positive") {
          return pos > neg; // "more positive than negative"
        }
        if (filterSentiment === "Negative") {
          return neg > pos; // "more negative than positive"
        }
        if (filterSentiment === "Neutral") {
          // "same logic" -> dominant or safe logic: Neutral is tied or greater than others
          return neu >= pos && neu >= neg;
        }
        return false;
      });

      return filteredEntities.map((content) => ({
        ...content,
        profile,
        type: entityType,
      }));
    }

    // ✅ MODE 3: Filter by Date
    if (date) {
      return datasets.flatMap((profile) => {
        const contentField = getContentField(profile.platform);
        const contentArray = (profile.data || []).flatMap((entry) =>
          entry.date === date ? entry[contentField] || [] : []
        );
        const entityType = profile.platform === "YouTube" ? "video" : "post";
        return contentArray.map((content) => ({
          ...content,
          profile,
          type: entityType,
        }));
      });
    }

    // ✅ MODE 4: Filter by Day of Week
    if (filterDayOfWeek) {
      return datasets.flatMap((profile) => {
        const contentField = getContentField(profile.platform);
        const entityType = profile.platform === "YouTube" ? "video" : "post";

        const matchingEntries = (profile.data || []).filter(entry =>
          dayjs(entry.date).format('dddd') === filterDayOfWeek
        );

        const contentArray = matchingEntries.flatMap(entry => entry[contentField] || []);

        return contentArray.map((content) => ({
          ...content,
          profile,
          type: entityType,
        }));
      });
    }

    return [];
  }, [datasets, date, filterProfileId, filterContentType, filterDayOfWeek, filterSentiment]); // ✅ Add dependency
  // ⬆️=========================================
  // ⬆️ END OF UPDATED LOGIC
  // ⬆️=========================================

  // (handleViewAnalytics/handleDetailsModalClose are unchanged)
  const handleViewAnalytics = (entity) => {
    setSelectedEntity(entity);
    setIsTransitioningToDetails(true);
    setShowCard(false);
    setIsDetailsModalOpen(true);
  };
  const handleDetailsModalClose = () => {
    setIsDetailsModalOpen(false);
    setShowCard(true);
    setIsTransitioningToDetails(false);
  };

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [date, isModalOpen, filterProfileId, filterContentType, filterDayOfWeek, filterSentiment]); // ✅ Added new dependency

  // (Slice data, image height, and page change are unchanged)
  useEffect(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    setPageData(flattenedEntities.slice(start, end));
    imageHeightsRef.current = [];
    setForcedHeight(250);
  }, [flattenedEntities, currentPage]);

  useEffect(() => {
    if (pageData.length > 0 && imageHeightsRef.current.length === pageData.length) {
      const counts = {};
      imageHeightsRef.current.forEach((h) => {
        counts[h] = (counts[h] || 0) + 1;
      });
      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      const MAX_ALLOWED_HEIGHT = 300;
      setForcedHeight(sorted.length > 0 ? Math.min(Number(sorted[0][0]), MAX_ALLOWED_HEIGHT) : 250);
    }
  }, [pageData]);

  const registerHeight = (height) => {
    imageHeightsRef.current.push(Math.round(height));
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const totalPages = Math.ceil(flattenedEntities.length / ITEMS_PER_PAGE);

  // ✅ UPDATED: Dynamic title
  const formattedDate = date ? dayjs(new Date(date)).format("MMM D, YYYY") : "";
  const popupTitle = filterContentType
    ? `Content Type: ${filterContentType}`
    : filterSentiment
      ? `Sentiment: ${filterSentiment}`
      : filterDayOfWeek
        ? `All Content for ${filterDayOfWeek}s` // ✅ Add new title
        : (date ? `Published on ${formattedDate}` : "Entities");

  const slideDirection = "up";

  return (
    <>
      <Backdrop
        open={isModalOpen || showCard}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: "rgba(0, 0, 0, 0.85)",
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        <Slide
          direction={slideDirection}
          in={showCard}
          mountOnEnter
          unmountOnExit
        >
          <Card
            sx={{
              position: "fixed",
              bottom: 0,
              transform: "translateX(-50%)",
              width: { xs: "95%", sm: "90%", md: "90%", lg: "90%" },
              maxWidth: "1400px",
              maxHeight: "85vh",
              overflowY: "auto",
              borderRadius: 3,
              padding: 4,
              boxShadow: 24,
              backgroundColor: "white",
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleCloseClick}
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 1201 }}
            >
              <CloseIcon />
            </IconButton>

            {/* ✅ UPDATED: Header to use dynamic title */}
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              sx={{
                borderBottom: "1px solid #e0e0e0",
                position: "sticky",
                top: 0,
                backgroundColor: "white",
                zIndex: 100,
              }}
            >
              <MDTypography variant="h5">{popupTitle}</MDTypography>
            </MDBox>

            {/* Content List */}
            <MDBox px={1} py={3} sx={{ flexGrow: 1, overflowY: "auto" }}>
              {pageData.length > 0 ? (
                <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={2} px={2}>
                  {pageData.map((entity, index) => (
                    <MDBox
                      sx={{
                        flex: "1 1 calc(20% - 16px)",
                        minWidth: "280px",
                        maxWidth: "300px",
                        minHeight: "450px",
                      }}
                      key={entity.id || entity._id || `${entity.profile?.id || 'profile'}-${index}`}
                    >
                      <EntityCard
                        entityObject={entity}
                        isPopup={true}
                        profileObject={entity.profile}
                        platform={entity.profile.platform}
                        entityType={entity.type}
                        forcedHeight={forcedHeight}
                        registerHeight={registerHeight}
                        onViewAnalytics={() => handleViewAnalytics(entity)}
                      />
                    </MDBox>
                  ))}
                </MDBox>
              ) : (
                <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <MDTypography variant="body2" color="text">
                    No content found for this selection.
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            {/* Pagination */}
            <MDBox display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.max(totalPages, 1)}
                page={currentPage}
                onChange={handlePageChange}
                color="accent"
                siblingCount={0}
                boundaryCount={1}
                hidePrevButton={false}
                hideNextButton={false}
              />
            </MDBox>
          </Card>
        </Slide>
      </Backdrop>

      {/* Details Popup (unchanged) */}
      {isDetailsModalOpen && selectedEntity && (
        <EntityDetailsPopup
          isModalOpen={isDetailsModalOpen}
          onClose={handleDetailsModalClose}
          entityObject={selectedEntity}
          useBackdrop={false}
        />
      )}
    </>
  );
}

// ✅ UPDATED: Add new prop types
EntitiesPopup.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  datasets: PropTypes.array.isRequired,
  date: PropTypes.string,
  filterProfileId: PropTypes.string,
  filterContentType: PropTypes.string,
  filterDayOfWeek: PropTypes.string,
  filterSentiment: PropTypes.string, // ✅ New prop
};

export default EntitiesPopup;